/**
 * ThumbnailRegenerateDialog — modal that lets the user customize
 * the storyboard's YouTube thumbnail BEFORE re-rendering it via
 * gpt-image-1.
 *
 * Two knobs:
 *   1. Cast picker — add / remove characters from the user's
 *      Character Library. The selection replaces the storyboard's
 *      `character_ids[]`, which the thumbnail generator's cast
 *      override reads at render time. Empty selection = "strip the
 *      cast" (fall back to channel-level default characters).
 *   2. Revision notes — free-form text like "close-up on Bella",
 *      "remove the sunscreen bottle", "make the sky darker". The
 *      note is appended to the prompt as the highest-priority
 *      section so the model treats it as an edit request.
 *
 * Submits to `POST /api/storyboards/{id}/thumbnail/generate` with
 * `{ character_ids, notes }`. Long-running (60-90s) — we hand
 * control back to the parent's regenerate flow via `onConfirm`
 * which shows the render spinner + polls for the new URL.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, Wand2, Sparkles } from "lucide-react";
import {
  listCharacters,
  characterImageUrl,
} from "@/lib/charactersApi";
import StylePresetPicker from "@/components/StylePresetPicker";

export default function ThumbnailRegenerateDialog({
  board,
  onClose,
  onConfirm,
}) {
  const [characters, setCharacters] = useState([]);
  const [picked, setPicked] = useState(() => board?.character_ids || []);
  const [notes, setNotes] = useState("");
  const [stylePreset, setStylePreset] = useState(
    () => board?.style_preset || "",
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listCharacters()
      .then((list) => setCharacters(list || []))
      .catch(() => setCharacters([]))
      .finally(() => setLoading(false));
  }, []);

  const togglePick = (id) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const pickedSummary = useMemo(() => {
    if (!picked.length) return "";
    const names = characters
      .filter((c) => picked.includes(c.character_id))
      .map((c) => c.name);
    return names.join(", ");
  }, [picked, characters]);

  const submit = async (opts = {}) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        characterIds: picked,
        notes,
        stylePreset,
        fromScratch: !!opts.fromScratch,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate thumbnail");
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  return (
    <div
      data-testid="thumbnail-regen-dialog"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
    >
      <div
        className="relative bg-zinc-950 border border-zinc-800 rounded-sm max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="thumbnail-regen-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-yellow-400"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-zinc-900">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
            regenerate thumbnail
          </div>
          <h2 className="text-white text-2xl font-black mb-1 pr-8">
            Customize before regenerating
          </h2>
          <p className="text-zinc-400 text-sm">
            Pick which characters should appear + describe any changes.
            The AI will re-render with these instructions.
          </p>
        </div>

        {/* Character picker */}
        <div className="p-6 border-b border-zinc-900">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
              Cast on this thumbnail
            </div>
            {picked.length > 0 && (
              <button
                type="button"
                onClick={() => setPicked([])}
                data-testid="thumbnail-regen-clear-cast"
                className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-400"
              >
                clear all
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-xs text-zinc-600 font-mono">
              [recon] loading cast<span className="blink">_</span>
            </div>
          ) : characters.length === 0 ? (
            <div
              data-testid="thumbnail-regen-cast-empty"
              className="border border-dashed border-zinc-800 rounded-sm py-4 px-3 text-xs text-zinc-500"
            >
              You have no characters yet. Build a reusable cast in the{" "}
              <Link
                to="/characters"
                className="text-yellow-400 hover:underline"
              >
                Character library
              </Link>
              , then return here.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
              {characters.map((c) => {
                const isSelected = picked.includes(c.character_id);
                const cover = c.images?.[0];
                return (
                  <button
                    type="button"
                    key={c.character_id}
                    onClick={() => togglePick(c.character_id)}
                    data-testid={`thumbnail-regen-cast-toggle-${c.character_id}`}
                    className={`flex items-center gap-3 p-2 border rounded-sm transition-colors text-left ${
                      isSelected
                        ? "border-yellow-400 bg-yellow-400/10"
                        : "border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <div className="h-9 w-9 shrink-0 rounded-sm overflow-hidden bg-zinc-900 border border-zinc-800">
                      {cover ? (
                        <img
                          src={characterImageUrl(c.character_id, cover.image_id)}
                          alt={c.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-yellow-400 font-bold">
                          {c.name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm font-bold truncate">
                        {c.name}
                      </div>
                      {c.role && (
                        <div className="font-mono text-[9px] uppercase tracking-wider text-yellow-400/80">
                          {c.role}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-yellow-400 font-mono text-xs shrink-0">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {pickedSummary && (
            <div
              data-testid="thumbnail-regen-picked-summary"
              className="mt-3 font-mono text-[10px] uppercase tracking-wider text-yellow-400/80"
            >
              on-screen cast: {pickedSummary}
            </div>
          )}
        </div>

        {/* Style preset */}
        <div className="p-6 border-b border-zinc-900">
          <StylePresetPicker
            value={stylePreset}
            onChange={setStylePreset}
            label="Visual style"
            helpText="Applied to this thumbnail. Changing this also updates the storyboard's style for future scene renders."
            testId="thumbnail-regen-style-preset"
          />
        </div>

        {/* Revision notes */}
        <div className="p-6">
          <label
            htmlFor="thumb-notes"
            className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2"
          >
            Revision notes{" "}
            <span className="text-zinc-600 normal-case tracking-normal">
              (optional — describe any changes)
            </span>
          </label>
          <textarea
            id="thumb-notes"
            data-testid="thumbnail-regen-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 800))}
            rows={3}
            placeholder="e.g. Close-up on Bella's face, remove the sunscreen bottle, add a beach ball in the foreground, warmer sunset lighting."
            className="w-full bg-black border border-zinc-700 text-zinc-200 px-3 py-2 rounded-sm focus:border-yellow-400 outline-none text-sm font-mono leading-relaxed"
          />
          <div className="mt-1 text-[10px] text-zinc-600 font-mono">
            {notes.length}/800 chars — the AI treats these as the
            highest-priority instructions.
          </div>
        </div>

        <div className="p-6 border-t border-zinc-900 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => submit()}
            disabled={submitting}
            data-testid="thumbnail-regen-confirm"
            className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-wide px-5 py-2.5 rounded-sm text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Rendering…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Regenerate (channel style)
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => submit({ fromScratch: true })}
            disabled={submitting}
            data-testid="thumbnail-regen-from-scratch"
            title="Ignore the channel's reference thumbnails entirely and let gpt-image-1 compose freely from just the title + description. Best for when regenerates keep producing the same portrait."
            className="inline-flex items-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-wide px-5 py-2.5 rounded-sm text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-4 h-4" />
            Try from scratch
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="thumbnail-regen-cancel"
            className="font-mono text-xs uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-400 rounded-sm hover:border-zinc-500 ml-auto"
          >
            Cancel
          </button>
        </div>
        <div className="px-6 pb-4 -mt-1 font-mono text-[10px] text-zinc-500 leading-relaxed">
          <span className="text-emerald-300">Try from scratch</span> skips the
          channel style refs — same feel as prompting ChatGPT directly with
          your title and description. Best for magazine-infographic-style
          thumbnails; can drift from your channel&apos;s visual language.
        </div>
      </div>
    </div>
  );
}
